import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CircleCTA } from "../components/CircleCTA";
import { AgentChat } from "../components/AgentChat";
import { useScrollReveal } from "../hooks/useScrollReveal";
import { useSite } from "../contexts/SiteContext";
import { fetchProducts } from "../lib/api";
import {
  Workflow,
  Bot,
  Database,
  Sparkles,
  Cpu,
  ArrowUpRight,
  Check,
  Layers,
  Mail,
} from "lucide-react";

const stats = [
  { value: "120+", label: "Workflows shipped" },
  { value: "8.4×", label: "Avg ops speedup" },
  { value: "24/7", label: "Always-on agents" },
  { value: "30d", label: "Time to value" },
];

const ICON_MAP = {
  Workflow,
  Bot,
  Database,
  Sparkles,
  Cpu,
  ArrowUpRight,
  Check,
  Layers,
  Mail,
};

const FALLBACK_USE_CASES = [
  {
    icon: "Workflow",
    title: "Ops Autopilot",
    body: "Invoices, CRM sync, lead routing, internal Slack triage — automated end-to-end.",
  },
  {
    icon: "Bot",
    title: "AI Support Agents",
    body: "Voice + chat agents that resolve 70% of tickets and hand off cleanly to humans.",
  },
  {
    icon: "Database",
    title: "Data Pipelines",
    body: "ETL, dashboards, forecasting. Your warehouse stops being a graveyard.",
  },
  {
    icon: "Sparkles",
    title: "Growth Engine",
    body: "Enrichment, multichannel sequences, AI-personalized outreach that books meetings.",
  },
];

const steps = [
  { n: "01", title: "Map", body: "We audit your workflows and find the highest-ROI automations." },
  { n: "02", title: "Build", body: "Our pod ships production-grade AI workflows in 2–4 weeks." },
  { n: "03", title: "Operate", body: "We monitor, evaluate, and improve — you keep your time back." },
];

export const Home = ({ onOpenLead }) => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const revealRef = useScrollReveal(".in-view", [products]);
  const { settings } = useSite();
  const hero = settings?.hero || {};

  useEffect(() => {
    fetchProducts()
      .then((d) => {
        if (d && d.items && d.items.length > 0) {
          const mapped = d.items.map((p) => ({
            icon: p.icon || "Layers",
            title: p.title,
            body: p.tagline,
          }));
          setProducts(mapped);
        } else {
          setProducts(FALLBACK_USE_CASES);
        }
      })
      .catch(() => {
        setProducts(FALLBACK_USE_CASES);
      });
  }, []);

  const displayProducts = products.length > 0 ? products : FALLBACK_USE_CASES;

  return (
    <div className="relative" data-testid="home-page" ref={revealRef}>
      {/* HERO */}
      <section className="relative min-h-[100vh] flex flex-col items-center justify-center overflow-hidden pt-24 pb-24 water-blobs">
        <div className="absolute inset-0 bg-radial-glow" />
        <div className="absolute inset-0 bg-grid" />
        <div className="absolute inset-0 water-shimmer" />
        <div className="absolute inset-0 noise" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 w-full flex flex-col items-center text-center">
          <div
            className="reveal reveal-1 inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass text-xs tracking-[0.2em] uppercase font-bold text-zinc-200"
            data-testid="hero-eyebrow"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#00F0FF] pulse-glow" />
            {hero.eyebrow || "AI Automation Studio · Reness AI"}
          </div>

          <h1
            className="reveal reveal-2 font-display font-black uppercase tracking-tighter mt-8 text-gradient leading-[0.85]"
            style={{ fontSize: "clamp(3rem, 13vw, 11rem)" }}
            data-testid="hero-headline"
          >
            <span className="block">{hero.headline_top || "I WANT"}</span>
            <span className="block">
              {hero.headline_mid || "TO"}{" "}
              <span className="text-gradient-neon">
                {hero.headline_accent || "AUTOMATE"}
              </span>
            </span>
          </h1>

          <p
            className="reveal reveal-3 mt-8 max-w-2xl text-zinc-400 text-base md:text-lg leading-relaxed"
            data-testid="hero-subline"
          >
            {hero.subline || "We help ambitious teams replace busywork with AI workflows that ship, scale, and pay for themselves."}
          </p>

          <div className="reveal reveal-4 mt-12 flex flex-col items-center gap-4">
            <CircleCTA onClick={() => onOpenLead?.()} />
            <p className="text-xs uppercase tracking-[0.3em] text-zinc-500 mt-2">
              tap the button · 60-second intake · sound on
            </p>
          </div>

          {/* AI Agent Chat */}
          <div className="reveal reveal-5 mt-16 w-full">
            <div className="text-center mb-6">
              <p className="text-xs tracking-[0.3em] uppercase text-[#00F0FF] font-bold">
                Or — chat with the agent
              </p>
              <h2 className="font-display text-2xl md:text-3xl font-black text-white mt-2 tracking-tight">
                Ask. Get a solution. Grounded in live web results.
              </h2>
            </div>
            <AgentChat />
          </div>
        </div>

        {/* Marquee */}
        <div className="absolute bottom-0 left-0 right-0 overflow-hidden border-t border-white/5 bg-[#020205]/40 backdrop-blur-md py-4">
          <div className="flex whitespace-nowrap animate-marquee gap-12 text-white/10 font-display font-black uppercase text-3xl md:text-5xl tracking-tighter">
            {Array.from({ length: 2 }).map((_, k) => (
              <div key={k} className="flex gap-12 shrink-0">
                <span>Ops Autopilot</span>
                <span>·</span>
                <span>AI Support</span>
                <span>·</span>
                <span>Growth Engine</span>
                <span>·</span>
                <span>Data Pipelines</span>
                <span>·</span>
                <span>Agentic Systems</span>
                <span>·</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="relative max-w-7xl mx-auto px-6 md:px-10 py-20" data-testid="stats-section">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((s) => (
            <div
              key={s.label}
              className="in-view glass rounded-2xl p-6 hover:border-[#00F0FF]/40 transition-colors"
              data-testid={`stat-${s.label.replace(/\s/g, "-").toLowerCase()}`}
            >
              <p className="font-display text-4xl md:text-5xl font-black text-white">
                {s.value}
              </p>
              <p className="text-zinc-500 text-xs uppercase tracking-[0.2em] mt-2">
                {s.label}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* USE CASES */}
      <section className="relative max-w-7xl mx-auto px-6 md:px-10 py-20" data-testid="usecases-section">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-[#00F0FF] font-bold">
              What we ship
            </p>
            <h2 className="font-display text-4xl md:text-5xl font-black text-white mt-3 tracking-tight">
              Automations that actually ship.
            </h2>
          </div>
          <p className="max-w-md text-zinc-400 text-sm">
            Four pillars that cover 90% of the leverage in modern teams. Pick
            one — or stack them all.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {displayProducts.map((u, index) => {
            const IconComponent = ICON_MAP[u.icon] || Layers;
            return (
              <div
                key={u.title + index}
                data-testid={`usecase-${u.title.replace(/\s/g, "-").toLowerCase()}`}
                className="in-view group relative glass rounded-3xl p-8 hover:border-[#00F0FF]/50 transition-colors overflow-hidden"
              >
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-[radial-gradient(circle_at_30%_0%,rgba(0,255,102,0.10),transparent_60%)] pointer-events-none" />
                <div className="relative">
                  <div className="w-11 h-11 rounded-xl bg-[#00F0FF]/10 border border-[#00F0FF]/30 flex items-center justify-center">
                    <IconComponent className="w-5 h-5 text-[#00F0FF]" strokeWidth={1.8} />
                  </div>
                  <h3 className="font-display text-2xl font-bold text-white mt-5">
                    {u.title}
                  </h3>
                  <p className="text-zinc-400 mt-2 leading-relaxed text-sm">
                    {u.body}
                  </p>
                  <button
                    onClick={() => navigate("/product")}
                    data-testid={`usecase-link-${u.title.replace(/\s/g, "-").toLowerCase()}`}
                    className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-white group-hover:text-[#00F0FF] transition-colors"
                  >
                    Explore <ArrowUpRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* PROCESS */}
      <section className="relative max-w-7xl mx-auto px-6 md:px-10 py-20" data-testid="process-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-4">
            <p className="text-xs tracking-[0.3em] uppercase text-[#00F0FF] font-bold">
              Process
            </p>
            <h2 className="font-display text-4xl md:text-5xl font-black text-white mt-3 tracking-tight leading-[1.05]">
              Three steps to a quieter team.
            </h2>
            <p className="text-zinc-400 mt-5 text-sm leading-relaxed">
              No 12-month roadmaps. We map, build, and operate — so your team
              gets time back, this quarter.
            </p>
            <button
              onClick={() => onOpenLead?.()}
              data-testid="process-cta-btn"
              className="mt-8 bg-white text-black px-6 py-3 rounded-full text-sm font-bold hover:bg-[#00F0FF] transition-colors inline-flex items-center gap-2"
            >
              Start with a free audit <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-3 gap-5">
            {steps.map((s) => (
              <div
                key={s.n}
                className="in-view glass rounded-2xl p-6 hover:border-white/30 transition-colors"
                data-testid={`process-step-${s.n}`}
              >
                <p className="font-mono text-xs text-[#00F0FF] tracking-widest">
                  {s.n}
                </p>
                <h4 className="font-display text-2xl font-bold text-white mt-2">
                  {s.title}
                </h4>
                <p className="text-zinc-400 mt-2 text-sm leading-relaxed">
                  {s.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROOF */}
      <section className="relative max-w-7xl mx-auto px-6 md:px-10 py-20" data-testid="proof-section">
        <div className="in-view glass-strong rounded-3xl p-10 md:p-16 relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(0,255,102,0.08),transparent_50%)] pointer-events-none" />
          <div className="relative grid grid-cols-1 md:grid-cols-2 gap-10">
            <div>
              <Cpu className="w-7 h-7 text-[#00F0FF]" strokeWidth={1.6} />
              <h3 className="font-display text-3xl md:text-4xl font-black text-white mt-5 leading-tight">
                "Replaced 3 full-time roles with one Automate.AI workflow stack."
              </h3>
              <p className="text-zinc-400 mt-4 text-sm">
                — Chief of Staff, Series B fintech
              </p>
            </div>
            <ul className="space-y-3">
              {[
                "Production-ready code, not prompts",
                "We own the infra, eval, and observability",
                "Fixed scope, fixed price, every sprint",
                "Direct Slack channel with our engineers",
              ].map((t) => (
                <li
                  key={t}
                  className="flex items-start gap-3 text-zinc-200 text-sm"
                >
                  <Check className="w-5 h-5 text-[#00F0FF] mt-0.5 shrink-0" />
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
